import { useCallback, useEffect } from 'react';
import { googleDataLayerPush } from '../utils/analytics';
import { modelNextStepsVisible, seriesNextStepsVisible } from '../constants/analytics.constants';
import useSitecoreContext from './useSitecoreContext';

const useAnalyticsActionOnScroll = (
  refElement: React.MutableRefObject<HTMLDivElement | null>,
  event: string,
  title?: string | undefined,
  isEditorial?: boolean,
  productLine?: string | undefined,
  sendEvent?: boolean
): void => {
  let isActionExecuted = false;
  const { sitecoreContext } = useSitecoreContext();
  const titleValue = title || undefined;
  const excludeTitleEvents = [seriesNextStepsVisible, modelNextStepsVisible];

  const callAnalyticsAction = () => {
    if (isActionExecuted) {
      return;
    }

    isActionExecuted = true;
    let product_line = productLine;
    if (!productLine) {
      product_line = sitecoreContext?.analytics?.['productLine']
        ? sitecoreContext?.analytics?.['productLine']
        : undefined;
    }
    const dataLayerData = isEditorial
      ? { event: event, title: titleValue }
      : {
          event: event,
          product_line,
        };

    if (!isEditorial && excludeTitleEvents.indexOf(event) == -1 && titleValue) {
      dataLayerData['title'] = titleValue;
    }

    if (sitecoreContext?.analytics?.productSeries) {
      dataLayerData['product_series'] = sitecoreContext?.analytics?.productSeries;
    }

    if (sitecoreContext?.analytics?.productModel) {
      dataLayerData['product_model'] = sitecoreContext?.analytics?.productModel;
    }

    if (sendEvent) {
      googleDataLayerPush(dataLayerData);
    }
  };

  const handleIfIsIntersectedElement = (element) => {
    if (!element) return;

    if (element.isIntersecting) {
      callAnalyticsAction();
    }
  };

  const handleIntersector = (entries) => {
    entries.forEach((element) => handleIfIsIntersectedElement(element));
  };

  const analyticsActionCallback = useCallback(handleIntersector, [event, productLine]);

  useEffect(() => {
    if (!refElement?.current) return;
    const observer = new IntersectionObserver(analyticsActionCallback, { threshold: 0.01 });
    observer.observe(refElement?.current);

    return (): void => observer.disconnect();
  }, [analyticsActionCallback, refElement]);
};

export default useAnalyticsActionOnScroll;
